"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useClickAway, useKeyPressEvent } from "react-use";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiUpdateWaveRequest } from "@/generated/models/ApiUpdateWaveRequest";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { convertWaveToUpdateWave } from "@/helpers/waves/waves.helpers";
import { commonApiPost } from "@/services/api/common-api";
import { multiPartUpload } from "../../create-wave/services/multiPartUpload";
import CreateWaveImageInput from "../../create-wave/overview/CreateWaveImageInput";
export default function WaveHeaderPictureEditModal(t0) {
  const $ = _c(40);
  const {
    wave,
    onClose
  } = t0;
  const modalRef = useRef(null);
  useClickAway(modalRef, onClose);
  useKeyPressEvent("Escape", onClose);
  const {
    setToast,
    requestAuth
  } = useContext(AuthContext);
  const {
    onWaveCreated
  } = useContext(ReactQueryWrapperContext);
  const [file, setFile] = useState(null);
  const [mutating, setMutating] = useState(false);
  const isDisabled = mutating || !file;
  let t1;
  if ($[0] !== wave) {
    t1 = async body => await commonApiPost({
      endpoint: `waves/${wave.id}`,
      body
    });
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onClose || $[3] !== onWaveCreated) {
    t2 = () => {
      onWaveCreated();
      onClose();
    };
    $[2] = onClose;
    $[3] = onWaveCreated;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== setToast) {
    t3 = error => {
      let message;
      if (error instanceof Error && error.message) {
        message = error.message;
      } else {
        if (typeof error === "string") {
          message = error;
        } else {
          if (typeof error.message === "string") {
            message = error.message;
          } else {
            try {
              message = JSON.stringify(error);
            } catch {
              message = "Failed to update wave picture";
            }
          }
        }
      }
      setToast({
        type: "error",
        message
      });
    };
    $[5] = setToast;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setMutating(false);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t5 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const editPictureMutation = useMutation(t5);
  let t6;
  if ($[12] !== editPictureMutation || $[13] !== file || $[14] !== requestAuth || $[15] !== setToast || $[16] !== wave) {
    t6 = async e => {
      e.preventDefault();
      if (!file) {
        return;
      }
      setMutating(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setToast({
          type: "error",
          message: "Failed to authenticate"
        });
        setMutating(false);
        return;
      }
      ;
      try {
        const uploaded = await multiPartUpload({
          file,
          path: "wave"
        });
        const body_0 = {
          ...convertWaveToUpdateWave(wave),
          picture: uploaded.url
        };
        await editPictureMutation.mutateAsync(body_0);
      } catch (t7) {
        const error_0 = t7;
        setToast({
          type: "error",
          message: (error_0 as any)?.message ?? "Failed to update wave picture"
        });
        setMutating(false);
      }
    };
    $[12] = editPictureMutation;
    $[13] = file;
    $[14] = requestAuth;
    $[15] = setToast;
    $[16] = wave;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const onSubmit = t6;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50" />;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div><p className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">Update wave picture</p><p className="tw-mb-0 tw-text-sm tw-text-iron-400">Upload a new image (JPG, JPEG, PNG, GIF, WEBP — max 10MB).</p></div>;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== file) {
    t9 = <CreateWaveImageInput imageToShow={file} setFile={setFile} allowRemove={false} />;
    $[20] = file;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== onClose) {
    t10 = <button type="button" onClick={onClose} className="tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition tw-duration-200 tw-ease-out hover:tw-bg-iron-800 sm:tw-w-auto">Cancel</button>;
    $[22] = onClose;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = `tw-relative tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-transition tw-duration-300 tw-ease-out sm:tw-w-auto ${isDisabled ? "tw-cursor-not-allowed tw-border-iron-800 tw-bg-iron-800 tw-text-iron-500" : "tw-cursor-pointer tw-border-primary-500 tw-bg-primary-500 tw-text-white hover:tw-border-primary-600 hover:tw-bg-primary-600"}`;
  const t12 = mutating ? "hidden" : "visible";
  let t13;
  if ($[24] !== t12) {
    t13 = <div style={{
      visibility: t12
    }}>Save</div>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== mutating) {
    t14 = mutating && <><span className="tw-sr-only">Uploading…</span><svg role="output" aria-label="Uploading" className="tw-absolute tw-inline tw-h-5 tw-w-5 tw-animate-spin tw-text-primary-400" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path className="tw-text-iron-600" d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" /></svg></>;
    $[26] = mutating;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== isDisabled || $[29] !== t11 || $[30] !== t13 || $[31] !== t14) {
    t15 = <button disabled={isDisabled} type="submit" className={t11}>{t13}{t14}</button>;
    $[28] = isDisabled;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t10 || $[34] !== t15) {
    t16 = <div className="tw-flex tw-justify-end tw-gap-x-3">{t10}{t15}</div>;
    $[33] = t10;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== onSubmit || $[37] !== t16 || $[38] !== t9) {
    t17 = createPortal(<div className="tw-relative tw-z-50">{t7}<div className="tw-fixed tw-inset-0 tw-z-10 tw-overflow-y-auto"><div className="tw-flex tw-min-h-full tw-items-end tw-justify-center tw-p-2 tw-text-center sm:tw-items-center lg:tw-p-0"><div ref={modalRef} className="tw-relative tw-w-full tw-max-w-full tw-transform tw-rounded-xl tw-bg-iron-950 tw-p-6 tw-text-left tw-shadow-xl tw-transition-all tw-duration-500 sm:tw-w-full md:tw-max-w-xl lg:tw-p-8"><form onSubmit={onSubmit} className="tw-flex tw-flex-col tw-gap-y-6">{t8}{t9}{t16}</form></div></div></div></div>, document.body);
    $[36] = onSubmit;
    $[37] = t16;
    $[38] = t9;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}